import { useEffect } from 'react';
import { track } from '../lib/analytics';

// Defer setup until idle so we don't compete with hero animations on slow devices.
const idle = (cb) =>
  (typeof window !== 'undefined' && 'requestIdleCallback' in window
    ? window.requestIdleCallback(cb, { timeout: 2000 })
    : setTimeout(cb, 800));

export default function useAnalytics() {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const cleanups = [];
    const handle = idle(() => {
      // ---- 1. Scroll depth (25 / 50 / 75 / 100) ----
      const hit = new Set();
      const onScroll = () => {
        const h = document.documentElement;
        const max = h.scrollHeight - h.clientHeight;
        if (max <= 0) return;
        const pct = (h.scrollTop / max) * 100;
        for (const m of [25, 50, 75, 100]) {
          if (pct >= m && !hit.has(m)) {
            hit.add(m);
            track('scroll_depth', { percent: m });
          }
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      cleanups.push(() => window.removeEventListener('scroll', onScroll));

      // ---- 2. Section-view (first time a section becomes 50% visible) ----
      const seen = new Set();
      const sections = document.querySelectorAll('section[id]');
      if ('IntersectionObserver' in window && sections.length) {
        const io = new IntersectionObserver(
          (entries) => {
            for (const e of entries) {
              if (e.isIntersecting && !seen.has(e.target.id)) {
                seen.add(e.target.id);
                track('section_view', { section: e.target.id });
              }
            }
          },
          { threshold: 0.5 }
        );
        sections.forEach((s) => io.observe(s));
        cleanups.push(() => io.disconnect());
      }

      // ---- 3. Web vitals (LCP, CLS, FCP, TTFB) ----
      const vitals = {};
      const conn = navigator.connection || {};
      vitals.effective_type = conn.effectiveType || '';
      vitals.downlink = conn.downlink || 0;
      vitals.save_data = !!conn.saveData;

      try {
        const nav = performance.getEntriesByType('navigation')[0];
        if (nav) vitals.ttfb = Math.round(nav.responseStart);
      } catch {}

      if ('PerformanceObserver' in window) {
        try {
          const fcpObs = new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
              if (entry.name === 'first-contentful-paint') {
                vitals.fcp = Math.round(entry.startTime);
              }
            }
          });
          fcpObs.observe({ type: 'paint', buffered: true });
          cleanups.push(() => fcpObs.disconnect());
        } catch {}

        try {
          const lcpObs = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            const last = entries[entries.length - 1];
            if (last) vitals.lcp = Math.round(last.startTime);
          });
          lcpObs.observe({ type: 'largest-contentful-paint', buffered: true });
          cleanups.push(() => lcpObs.disconnect());
        } catch {}

        try {
          let cls = 0;
          const clsObs = new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
              if (!entry.hadRecentInput) cls += entry.value;
            }
            vitals.cls = Math.round(cls * 1000) / 1000;
          });
          clsObs.observe({ type: 'layout-shift', buffered: true });
          cleanups.push(() => clsObs.disconnect());
        } catch {}
      }

      let sent = false;
      const flush = () => {
        if (sent) return;
        sent = true;
        track('web_vitals', vitals);
      };
      const onHide = () => {
        if (document.visibilityState === 'hidden') flush();
      };
      document.addEventListener('visibilitychange', onHide);
      window.addEventListener('pagehide', flush);
      cleanups.push(() => {
        document.removeEventListener('visibilitychange', onHide);
        window.removeEventListener('pagehide', flush);
      });
    });

    return () => {
      if (typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        try { window.cancelIdleCallback(handle); } catch {}
      }
      cleanups.forEach((fn) => { try { fn(); } catch {} });
    };
  }, []);
}
